import { useState } from "react";
import { useTranslation } from "react-i18next";
import { THINKER_FACET_ACTS } from "~/api/aiAdminTypes";
import { Select, TextArea } from "~/features/settings/controls";
import {
  type FacetSeatView,
  useFacetTry,
} from "~/features/settings/sections/thinkers/threads/threadsApi";
import { Button } from "~/foundation/components/Button";
import { Input } from "~/foundation/components/Input";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";

export type BindingKind = "none" | "role" | "capability" | "thinker";

/**
 * BRONCO Opus 03 — a facet as a SEAT TEMPLATE: who plays it (a role or a capability — the thinker economy picks the
 * thinker; or a pinned thinker), which room acts it may write, the seat it resolves to right now, and a Try that runs
 * this ONE facet as one seat on a slice of material — showing each change it wrote and whether the room took it.
 */
export function FacetSeat({
  owner,
  facetId,
  saved,
  bindingKind,
  bindingValue,
  acts,
  seat,
  onBinding,
  onActs,
}: {
  owner: { localId?: string; personaId?: string };
  /** The facet's id once saved (Try runs saved facets only). */
  facetId: string;
  saved: boolean;
  bindingKind: BindingKind;
  bindingValue: string;
  acts: string[];
  /** What the server says this facet's binding resolves to (after the last save). */
  seat: FacetSeatView["seat"] | null;
  onBinding: (kind: BindingKind, value: string) => void;
  onActs: (acts: string[]) => void;
}) {
  const { t } = useTranslation();
  const [material, setMaterial] = useState("");
  const tryFacet = useFacetTry();
  const toggle = (act: string) =>
    onActs(acts.includes(act) ? acts.filter((entry) => entry !== act) : [...acts, act]);
  const placeholder =
    bindingKind === "role"
      ? t("e.g. dev, analyst, qa")
      : bindingKind === "capability"
        ? t("e.g. act:check-small")
        : t("e.g. gemma4:26b or a local thinker");

  return (
    <div className="flex flex-col gap-3 rounded border border-divider p-2 md:col-span-2">
      <div className="grid gap-3 md:grid-cols-2">
        <Select
          label={t("Who plays it")}
          value={bindingKind}
          fullWidth
          hint={t(
            "A role or a capability lets the workspace pick the thinker; a pinned thinker always plays it.",
          )}
          onChange={(event) => onBinding(event.target.value as BindingKind, bindingValue)}
        >
          <option value="none">{t("This thinker itself")}</option>
          <option value="role">{t("A role")}</option>
          <option value="capability">{t("A capability")}</option>
          <option value="thinker">{t("A pinned thinker")}</option>
        </Select>
        {bindingKind !== "none" ? (
          <Input
            label={
              bindingKind === "role"
                ? t("Role")
                : bindingKind === "capability"
                  ? t("Capability")
                  : t("Thinker")
            }
            value={bindingValue}
            fullWidth
            placeholder={placeholder}
            onChange={(event) => onBinding(bindingKind, event.target.value)}
          />
        ) : null}
      </div>

      {seat ? (
        <p
          className={cn("text-small", seat.problem ? "text-danger" : "text-text-secondary")}
          role={seat.problem ? "alert" : undefined}
        >
          {seat.problem
            ? t("Cannot play right now: {{why}}", { why: seat.problem })
            : t("Plays on {{thinker}} ({{how}})", {
                thinker: seat.thinkerId ?? "—",
                how: seat.how ?? "",
              })}
        </p>
      ) : null}

      <fieldset className="flex flex-col gap-1">
        <legend className="text-small font-medium text-text">{t("Acts it may write")}</legend>
        <Text variant="small">{t("None ticked: whatever the room's phase asks of it.")}</Text>
        <div className="flex flex-wrap gap-3">
          {THINKER_FACET_ACTS.map((act) => (
            <label key={act} className="flex items-center gap-1.5 text-small text-text-secondary">
              <input type="checkbox" checked={acts.includes(act)} onChange={() => toggle(act)} />
              {t(act)}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-col gap-2">
        <TextArea
          label={t("Try this facet on some material")}
          value={material}
          rows={3}
          placeholder={t("Paste a few lines for this facet to look at")}
          onChange={(event) => setMaterial(event.target.value)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!saved || material.trim().length === 0 || tryFacet.isPending}
            onClick={() => tryFacet.mutate({ ...owner, facetId, material })}
          >
            {tryFacet.isPending ? t("Thinking…") : t("Try this facet")}
          </Button>
          {!saved ? (
            <Text variant="small">{t("Save the facets first; a try runs the saved facet.")}</Text>
          ) : null}
        </div>
        {tryFacet.isError ? (
          <p role="alert" className="text-small text-danger">
            {(tryFacet.error as Error).message}
          </p>
        ) : null}
        {tryFacet.data ? (
          <div className="flex flex-col gap-1 rounded bg-bg-secondary p-2 text-small">
            <span className="text-text-secondary">
              {t("Played on {{thinker}} · act {{act}}", {
                thinker: tryFacet.data.seat.thinkerId,
                act: tryFacet.data.act,
              })}
            </span>
            {tryFacet.data.error ? (
              <span className="text-danger">{tryFacet.data.error}</span>
            ) : null}
            {(tryFacet.data.deltas ?? []).length === 0 && !tryFacet.data.error ? (
              <span>{t("It wrote nothing.")}</span>
            ) : null}
            <ul className="flex flex-col gap-1">
              {(tryFacet.data.deltas ?? []).map((trace) => (
                <li
                  key={`${trace.delta.op}:${trace.delta.text ?? ""}:${trace.reason ?? ""}`}
                  className="flex flex-col"
                >
                  <span>
                    <strong>{trace.delta.op}</strong>
                    {trace.delta.kind ? ` ${trace.delta.kind}` : ""}
                    {trace.delta.text ? ` — ${trace.delta.text}` : ""}
                  </span>
                  <span className={trace.accepted ? "text-success" : "text-danger"}>
                    {trace.accepted
                      ? trace.raced
                        ? t(
                            "the room kept it as a dispute (written at the same moment as another seat)",
                          )
                        : t("the room accepts it")
                      : t("refused: {{why}}", { why: trace.reason ?? "" })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
